import { FC, PropsWithChildren, useLayoutEffect, useRef } from 'react'
import ReactDataSheet from 'react-datasheet'

import { SupplierMaxCapacity } from '@gateway.Supplier'
import { CellValue, GridElement, M4GridDataState } from './SufficiencyOfQuantity'
import { CodeName } from '../../../services/initializing/CodeName'

import 'react-datasheet/lib/react-datasheet.css'
import hearingMessage from '../../../data/hearing-message.json'
import { ConfirmReplyActions, useAppDispatch, useAppSelector } from '../../../stores'

const SheetRenderer: FC<
PropsWithChildren<{
  className: string
  simulationYm: string
  years: number
  months: number[]
  displayStartYear: string
}>
> = ({ className, simulationYm, years, months, children, displayStartYear }) => {
  const ref = useRef<HTMLDivElement>(null)
  const location = useAppSelector((state) => state.confirmreply.location.supplierMaxCapacityLocation)
  const dispatch = useAppDispatch()

  const createYearColumns = (startYear: string) => {
    const firstYear = Number(startYear)
    const emptyArray = Array.from({ length: years })

    return emptyArray.map<number>((_value, index) => {
      return firstYear + index
    })
  }

  useLayoutEffect(() => {
    ref.current?.scrollTo({ left: location })
  }, [location])

  return (
    <div
      ref={ref}
      className="scrollable-container"
      style={{ maxHeight: '61vh' }}
      onScroll={(event) => {
        dispatch(ConfirmReplyActions.setSupplierMaxCapacityLocation(Number(ref.current?.scrollLeft)))
      }}
    >
      <table
        className={className}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
              <div style={{ width: 30 }}>No</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 33 }}>
              <div style={{ width: 396 }}>管轄支店</div>
            </th>
            {createYearColumns(displayStartYear).map((year) => (
              <th colSpan={12} key={year}>
                {year}年度
              </th>
            ))}
          </tr>
          <tr>
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

const RowRenderer: FC<
PropsWithChildren<{
  rowNo: number
  rowLength: number
  supplierMaxCapacity: SupplierMaxCapacity
  branches: CodeName[]
}>
> = ({
  rowNo,
  rowLength,
  supplierMaxCapacity,
  branches,
  // right part
  children
}) => {
  /* データ変換 */
  const branchesName = (DATA: string) => {
    return branches.find((item) => item.CODE === DATA)?.NAME
  }

  const isLastRow = rowNo === rowLength - 1

  if (isLastRow) {
    return (
      <tr style={{ height: 33 }}>
        <th
          style={{ position: 'sticky', left: 0, textAlign: 'left' }}
          colSpan={2}
        >
          <span>合計</span>
        </th>
        {children}
      </tr>
    )
  }

  return (
    <tr style={{ height: 33 }}>
      <th style={{ position: 'sticky', left: 0 }}>{rowNo + 1}</th>
      <th style={{ position: 'sticky', left: 33 }}>
        <span>{branchesName(supplierMaxCapacity.branchCd)}</span>
      </th>
      {children}
    </tr>
  )
}

// セルの設定
const ValueViewer: FC<{
  valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  rowLength: number
}> = ({ valueViewerProps, rowLength }) => {
  const isLastRow = valueViewerProps.row === rowLength - 1

  return (
    <div
      style={{ backgroundColor: isLastRow ? '#99FF99' : '', height: '100%' }}
    >
      {valueViewerProps.value}
    </div>
  )
}

interface SufficiencyOfQuantityRateGridProps {
  gridDataState: M4GridDataState
  years: number
  months: number[]
  simulationYm: string
  branches: CodeName[]
  displayStartYear: string
}

export const SufficiencyOfQuantityRateGrid: FC<
SufficiencyOfQuantityRateGridProps
> = (props) => {
  /** Table Header */
  const sheetRenderer = (
    sheetRendererProps: ReactDataSheet.SheetRendererProps<
    GridElement,
    CellValue
    >
  ) => {
    return (
      <SheetRenderer
        className={sheetRendererProps.className}
        simulationYm={props.simulationYm}
        years={props.years}
        months={props.months}
        displayStartYear={props.displayStartYear}
      >
        {sheetRendererProps.children}
      </SheetRenderer>
    )
  }

  /** Table Row[Lift + Right(→rowRendererProps.children)] */
  const rowRenderer = (
    rowRendererProps: ReactDataSheet.RowRendererProps<
    GridElement,
    CellValue
    > & { selected: boolean }
  ) => {
    const supplierMaxCapacity: SupplierMaxCapacity =
      props.gridDataState.sufficiencyOfQuantityRateList[rowRendererProps.row]

    return (
      <RowRenderer
        rowNo={rowRendererProps.row}
        rowLength={props.gridDataState.grid.length}
        supplierMaxCapacity={supplierMaxCapacity}
        branches={props.branches}
      >
        {rowRendererProps.children}
      </RowRenderer>
    )
  }

  const valueViewer = (
    valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  ) => {
    return (
      <ValueViewer
        valueViewerProps={valueViewerProps}
        rowLength={props.gridDataState.grid.length}
      />
    )
  }

  return (
    <div>
      <h3 style={{ marginBottom: 0 }}>使用率（%）
        <span
            style={{ marginLeft: '16px', color: '#d51923', fontWeight: '700', fontSize: '16px' }}
        >
          {hearingMessage.sufficiencyOfQuantity.sufficiencyOfQuantityRateAnnotationInfo}
        </span>
      </h3>
      <ReactDataSheet<GridElement, CellValue>
        data={props.gridDataState.grid}
        sheetRenderer={sheetRenderer}
        rowRenderer={rowRenderer}
        valueRenderer={(cell) => cell.value}
        valueViewer={valueViewer}
      />
    </div>
  )
}
